import { useEffect, useState } from "react";
import type { Project } from "@proof/core";
import { useAuthoringServices } from "./services";
import { Button, Control, Modal } from "./components";

export function DocumentFormSource({
  project,
  label = "Generate form from document",
  onSelect,
}: {
  project: Project;
  label?: string;
  onSelect(project: Project, linked: boolean): void;
}) {
  const services = useAuthoringServices();
  const [open, setOpen] = useState(false),
    [query, setQuery] = useState(""),
    [id, setId] = useState(project.id),
    [rows, setRows] = useState<{ id: string; name: string }[]>([]),
    [linked, setLinked] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      void services
        .listFormProjects?.(query, controller.signal)
        .then((rows) => {
          if (!controller.signal.aborted) setRows(rows);
        })
        .catch((error) => {
          if (!controller.signal.aborted) setError(error.message);
        });
    }, 200);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [open, query, services.listFormProjects]);
  return (
    <>
      <Button
        onClick={() => {
          setError("");
          setOpen(true);
        }}
      >
        {label}
      </Button>
      {open && (
        <Modal
          title="Generate form from document"
          onClose={() => !busy && setOpen(false)}
        >
          <Control label="Search source projects">
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </Control>
          <Control label="Base this form on">
            <select value={id} onChange={(event) => setId(event.target.value)}>
              <option value={project.id}>
                {project.name} (current editor)
              </option>
              {rows
                .filter((row) => row.id !== project.id)
                .map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.name}
                  </option>
                ))}
            </select>
          </Control>
          <Control label="Future changes">
            <select
              value={linked ? "linked" : "copy"}
              onChange={(event) => setLinked(event.target.value === "linked")}
            >
              <option value="copy">Copy — assign to projects manually</option>
              <option value="linked">
                Link this source project — follow future form changes
              </option>
            </select>
          </Control>
          <p>
            {linked
              ? "Saving later versions of this global form updates matching input settings in the linked project's draft. Published proofs retain their saved versions until the project is published again. Document bindings and rules stay with the project."
              : "The generated form is independent. Saving it will not update the source project."}
          </p>
          {error && <p role="alert">{error}</p>}
          <div className="button-row">
            <Button disabled={busy} onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={busy || !id}
              onClick={async () => {
                setBusy(true);
                setError("");
                try {
                  const source =
                    id === project.id
                      ? project
                      : await services.loadFormProject?.(
                          id,
                          new AbortController().signal,
                        );
                  if (!source || source.tenantId !== project.tenantId)
                    throw new Error(
                      "Source project is unavailable in this workspace.",
                    );
                  if (
                    linked &&
                    !window.confirm(
                      `Future changes to this form will update matching fields in ${source.name}'s draft. Continue?`,
                    )
                  )
                    return;
                  onSelect(source, linked);
                  setOpen(false);
                } catch (error) {
                  setError((error as Error).message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy ? "Loading…" : "Generate Form"}
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
}
